/**
 * Backend environment definitions.
 *
 * Each entry represents a target backend the admin can point the app to.
 * The selected environment is carried in the URL query string (?env=<key>) so
 * it is explicit and survives login/logout redirects and localStorage resets.
 * Each environment authenticates against its OWN Entra tenant (see
 * src/config/msalConfig.js), so switching backends DOES switch the login realm.
 */
export const ENVIRONMENTS = {
  develop: {
    label: 'Develop',
    baseUrl: 'https://dash-pipelines-develop.azurewebsites.net',
    color: '#2e7d32', // green
  },
  staging: {
    label: 'Staging',
    baseUrl: 'https://dash-pipelines-staging.azurewebsites.net',
    color: '#ed6c02', // orange
  },
  production: {
    label: 'Production',
    baseUrl: 'https://dash-pipelines.azurewebsites.net',
    color: '#d32f2f', // red
  },
}


export const getDefaultEnvironmentKey = () => {
  return 'production'
}

// Memoized for the page's lifetime: the env only changes via a full reload
// (the selector) or a fresh load carrying ?env (login/logout redirects), so it
// is safe to capture once. This makes client-side navigations that drop the
// ?env param (sidebar links, post-login redirect) harmless.
let _envCache = null

/**
 * Resolve the active environment key. Priority: ?env=<key> in the URL, then the
 * value captured earlier this session (sessionStorage), then the default. The
 * URL is the source of truth so the choice survives redirects and a
 * localStorage clear on logout; sessionStorage backs up a refresh after a
 * client-side navigation dropped the param.
 */
export const getSelectedEnvKey = () => {
  if (_envCache) return _envCache
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('env')
    if (fromUrl && ENVIRONMENTS[fromUrl]) {
      _envCache = fromUrl
      try {
        window.sessionStorage.setItem('selectedEnv', fromUrl)
      } catch (e) {
        /* sessionStorage unavailable — ignore */
      }
      return _envCache
    }
    const stored = window.sessionStorage.getItem('selectedEnv')
    if (stored && ENVIRONMENTS[stored]) {
      _envCache = stored
      return _envCache
    }
  } catch (e) {
    // window/URL not available (SSR/tests) — fall through to default
  }
  _envCache = getDefaultEnvironmentKey()
  return _envCache
}

/**
 * Path to the login page that preserves the currently selected environment,
 * e.g. "/login?env=staging". Used by every redirect to /login so the env is
 * not lost across the auth flow.
 */
export const loginPathForCurrentEnv = () => `/login?env=${getSelectedEnvKey()}`

/**
 * Return the given URL string with ?env=<key> set, preserving other params.
 */
export const withEnvParam = (urlString, envKey) => {
  const url = new URL(urlString, window.location.origin)
  url.searchParams.set('env', envKey)
  return url.toString()
}
